import type { AppRole } from '../../../core/permissions/role-groups';
import {
  COORDINATION,
  HR,
  CLINICAL,
  OPERATIONS,
  STAFF_ALL,
  STOCK,
} from '../../../core/permissions/role-groups';

export interface WorkspaceTab {
  label: string;
  route: string;
  icon: string;
  roles?: readonly AppRole[];
  queryParams?: Readonly<Record<string, string>>;
}

export interface WorkspaceGroup {
  id: string;
  title: string;
  icon: string;
  tabs: readonly WorkspaceTab[];
  /** Routes de détail qui doivent conserver la navigation de l'espace. */
  detailPrefixes?: readonly string[];
}

const tab = (
  label: string,
  route: string,
  icon: string,
  roles?: readonly AppRole[],
  queryParams: Readonly<Record<string, string>> = {},
): WorkspaceTab => ({ label, route, icon, roles, queryParams });

/**
 * Espaces métier transversaux — uniquement ceux qui n'ont PAS de hub.
 *
 * L'application a longtemps porté deux navigations à onglets pour le même
 * contenu : le hub, atteint par le menu, et cette barre contextuelle, atteinte
 * par un favori ou la recherche. Selon la porte d'entrée on tombait sur deux
 * écrans différents pour « Laboratoire » ou « Stock ». Les routes concernées
 * redirigent désormais vers l'onglet du hub (voir `toHubTab` dans app.routes),
 * et leurs groupes ont disparu d'ici. Ne subsistent que les regroupements sans
 * hub — y ajouter une entrée qui vit déjà dans un hub recréerait le doublon.
 */
export const WORKSPACE_GROUPS: readonly WorkspaceGroup[] = [
  // « Accueil patient » était un groupe d'espace : trois routes distinctes
  // reliées par cette barre d'onglets. C'est devenu la page /reception-hub, qui
  // porte ses onglets elle-même — l'entrée de menu reste donc allumée quand on
  // passe d'une vue à l'autre, ce que trois routes séparées ne permettaient pas.
  {
    id: 'appointments',
    title: 'Rendez-vous',
    icon: 'pi pi-calendar',
    tabs: [
      tab('Liste', '/appointments', 'pi pi-list', COORDINATION),
      tab('Calendrier', '/calendar', 'pi pi-calendar-clock', STAFF_ALL),
    ],
    detailPrefixes: ['/appointments/'],
  },
  // « Consultations & documents médicaux » est devenu /consultations-hub, qui
  // porte ces quatre vues en onglets. Deux de ses anciennes routes
  // (/prescriptions, /medical-leave-forms) redirigent maintenant vers le hub :
  // les garder ici aurait pointé des onglets vers des redirections.
  {
    id: 'hospitalization',
    title: 'Hospitalisations',
    icon: 'pi pi-building',
    tabs: [
      tab('Séjours', '/hospitalization', 'pi pi-building', CLINICAL),
      tab('Lits', '/beds', 'pi pi-sliders-v', CLINICAL),
      tab('Soins infirmiers', '/nursing-care', 'pi pi-plus-circle', CLINICAL),
    ],
    detailPrefixes: ['/hospitalization/'],
  },
  // « Bloc opératoire » est devenu /bloc-hub (planning / interventions / salles / équipements).
  {
    id: 'purchasing',
    title: 'Achats pharmacie',
    icon: 'pi pi-shopping-bag',
    tabs: [
      tab('Commandes', '/pharmacy-purchase-orders', 'pi pi-file-edit', STOCK),
      tab('Fournisseurs', '/pharmacy-suppliers', 'pi pi-truck', STOCK),
    ],
  },
  {
    id: 'time-off',
    title: 'Temps & congés',
    icon: 'pi pi-calendar-clock',
    tabs: [
      tab('Planning', '/shifts', 'pi pi-calendar', HR),
      tab('Présences', '/attendances', 'pi pi-clock', HR),
      tab('Congés', '/leave-requests', 'pi pi-calendar-minus', HR),
    ],
  },
  {
    id: 'communication',
    title: 'Communication',
    icon: 'pi pi-envelope',
    tabs: [
      tab('Réclamations', '/complaints', 'pi pi-flag', OPERATIONS),
      tab('Messages', '/secure-messages', 'pi pi-envelope', STAFF_ALL),
    ],
  },
  // « Intégrations et sécurité » est devenu /integrations-hub.
];

const cleanRoute = (route: string): string => {
  const [withoutQuery] = route.split('?');
  const [withoutHash] = withoutQuery.split('#');
  return withoutHash.length > 1 && withoutHash.endsWith('/')
    ? withoutHash.slice(0, -1)
    : withoutHash;
};

/**
 * Routes qui rendent déjà leur propre sélecteur d'espace à onglets.
 * Le layout ne doit pas leur ajouter le fil d'Ariane global.
 */
export const EMBEDDED_WORKSPACE_ROUTES = new Set([
  '/reception-hub',
  '/bloc-hub',
  '/integrations-hub',
  '/ged-hub',
  '/consultations-hub',
  '/laboratory-hub',
  '/vaccinations-hub',
  '/blood-hub',
  '/maternity-hub',
  '/stock-hub',
  '/fleet',
  '/billing-hub',
  '/insurance-hub',
  '/accounting-hub',
  '/people-hub',
  '/reports',
  '/pharmacy-pos',
  // Page à sections : son en-tête et sa navigation verticale tiennent lieu de fil d'Ariane.
  '/organization',
]);

export const findWorkspaceGroup = (route: string): WorkspaceGroup | undefined => {
  const currentRoute = cleanRoute(route);
  return WORKSPACE_GROUPS.find(
    (group) =>
      group.tabs.some((item) => currentRoute === item.route) ||
      group.detailPrefixes?.some((prefix) => currentRoute.startsWith(prefix)),
  );
};

export const hasContextualNavigation = (route: string): boolean =>
  EMBEDDED_WORKSPACE_ROUTES.has(cleanRoute(route)) || !!findWorkspaceGroup(route);

export const visibleWorkspaceTabs = (
  group: WorkspaceGroup,
  role?: string,
): readonly WorkspaceTab[] => {
  if (role === 'super_admin') return group.tabs;
  return group.tabs.filter(
    (item) => !item.roles || (!!role && item.roles.includes(role as AppRole)),
  );
};
